import { Component, EventEmitter, OnDestroy, Output } from '@angular/core'
import { firstValueFrom } from 'rxjs'
import { AuthService } from '@shared/services/auth.service'
import { UserService } from '@shared/services/user.service'
import { callableErrorMessage } from '@shared/errors/callable-error-message'

@Component({
  selector: 'app-user-delete-popup',
  templateUrl: './user-delete-popup.component.html',
  styleUrls: ['./user-delete-popup.component.scss'],
  standalone: false,
})
export class UserDeletePopupComponent implements OnDestroy {
  @Output() close = new EventEmitter<void>()
  @Output() deleted = new EventEmitter<void>()

  password = ''
  isDeleting = false
  deletionCompleted = false
  errorMessage = ''
  private destroyed = false

  constructor(private userService: UserService, private authService: AuthService) {}

  ngOnDestroy(): void {
    this.destroyed = true
    this.password = ''
  }

  onClose(): void {
    if (!this.isDeleting && !this.deletionCompleted) {
      this.password = ''
      this.close.emit()
    }
  }

  async onDelete(): Promise<void> {
    if (this.isDeleting || this.destroyed || (!this.deletionCompleted && !this.password)) {
      return
    }

    this.isDeleting = true
    this.errorMessage = ''
    try {
      if (!this.deletionCompleted) {
        await this.authService.reauthenticateForDeletion(this.password)
        this.password = ''
        if (this.destroyed) {
          return
        }
        await firstValueFrom(this.userService.deleteUserSelf())
        this.deletionCompleted = true
      }
      await this.authService.signOut()
      if (!this.destroyed) {
        this.deleted.emit()
      }
    } catch (error) {
      this.errorMessage = this.deletionCompleted
        ? 'A fiókod törlése megtörtént, de a kijelentkezés nem sikerült. Próbáld újra a kijelentkezést.'
        : this.deletionErrorMessage(error)
    } finally {
      this.password = ''
      this.isDeleting = false
    }
  }

  private deletionErrorMessage(error: unknown): string {
    const code = (error as { code?: string })?.code
    switch (code) {
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
      case 'auth/invalid-login-credentials':
        return 'Nem megfelelő a jelszó. Add meg újra a jelenlegi jelszavad.'
      case 'auth/missing-password':
      case 'auth/requires-recent-login':
        return 'A törléshez erősítsd meg újra a bejelentkezésedet a jelenlegi jelszavaddal.'
      case 'auth/user-token-expired':
      case 'auth/user-disabled':
      case 'auth/user-not-found':
      case 'functions/unauthenticated':
        return 'A munkameneted lejárt. Jelentkezz be újra a fiókod törléséhez.'
      case 'auth/too-many-requests':
      case 'functions/resource-exhausted':
        return 'Túl sok próbálkozás történt. Kérjük, próbáld újra később.'
      case 'auth/network-request-failed':
      case 'functions/unavailable':
      case 'functions/deadline-exceeded':
        return 'A törlés eredménye nem erősíthető meg. Ellenőrizd az internetkapcsolatot, majd próbáld újra.'
      default:
        return callableErrorMessage(error, 'A fiók törlését nem sikerült megerősíteni. Kérjük, próbáld újra később.')
    }
  }
}
